
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setIsAuthenticated(true);
        setUserEmail(session.user.email || '');
      } else {
        setIsAuthenticated(false);
        setUserEmail('');
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setIsAuthenticated(true);
      setUserEmail(session.user.email || '');
    } else {
      setIsAuthenticated(false);
      setUserEmail('');
    }
    setIsLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setUserEmail('');
  };

  const getUserDisplayName = (email: string) => {
    if (!email) return '';
    const name = email.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-sm z-50 border-b border-gray-800">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-palette-line text-2xl text-white"></i>
            </div>
            <span className="text-xl font-bold text-white" style={{ fontFamily: '"Pacifico", serif' }}>
              ArtGallery
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === '/' 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Ana Sayfa
            </Link>
            <Link 
              to="/gallery" 
              className={`text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === '/gallery' 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Galeri
            </Link>
            <Link 
              to="/favorites" 
              className={`text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === '/favorites' 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Favoriler
            </Link>
            <Link 
              to="/order-tracking" 
              className={`text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === '/order-tracking' 
                  ? 'text-white' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Sipariş Takip
            </Link>
          </nav>

          {/* User Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-gray-300 border-t-white rounded-full animate-spin"></div>
            ) : isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full">
                    <i className="ri-user-line text-white text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-300">
                    Hoş geldin, <span className="text-white font-medium">{getUserDisplayName(userEmail)}</span>
                  </span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-gray-300 hover:text-white text-sm cursor-pointer flex items-center space-x-1"
                >
                  <i className="ri-logout-box-line"></i>
                  <span>Çıkış</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Giriş Yap
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white cursor-pointer"
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-800">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/' 
                    ? 'text-white' 
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Ana Sayfa
              </Link>
              <Link 
                to="/gallery" 
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/gallery' 
                    ? 'text-white' 
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Galeri
              </Link>
              <Link 
                to="/favorites" 
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/favorites' 
                    ? 'text-white' 
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Favoriler
              </Link>
              <Link 
                to="/order-tracking" 
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  location.pathname === '/order-tracking' 
                    ? 'text-white' 
                    : 'text-gray-300 hover:text-white'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Sipariş Takip
              </Link>
              
              {/* Mobile User Section */}
              <div className="pt-4 border-t border-gray-800">
                {isLoading ? (
                  <div className="flex justify-center">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-white rounded-full animate-spin"></div>
                  </div>
                ) : isAuthenticated ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full">
                        <i className="ri-user-line text-white text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-300">
                        Hoş geldin, <span className="text-white font-medium">{getUserDisplayName(userEmail)}</span>
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMenuOpen(false);
                      }}
                      className="text-gray-300 hover:text-white text-sm cursor-pointer flex items-center space-x-2"
                    >
                      <i className="ri-logout-box-line"></i>
                      <span>Çıkış Yap</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="block bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer text-center whitespace-nowrap"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Giriş Yap
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
